import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  TicketBatchDto,
  TicketCategory,
  TicketTransactionRecordDto,
  TicketCategoryCreateRequest,
  TicketCategoryEditRequest,
  CreateTicketCompositeRequest,
  TicketBatch,
  RecordInventoryBody,
} from '../../interfaces/inventory/inventory-management-interface';

interface AddCategoryRequest {
  storeId: number;
  ticketCategoryId: number;
  startDate: string;
  endDate?: string;
}

interface RemoveCategoryRequest {
  storeId: number;
  ticketCategoryId: number;
}

@Injectable({
  providedIn: 'root',
})
export class InventoryGatewayService {
  private ticketUrl = `${environment.api.url}/inventory/ticket`;
  private categoryUrl = `${environment.api.url}/inventory/ticket-categories`;

  constructor(private http: HttpClient) {}

  /** Fetch all tickets (batches + category + package + latest transaction) */
  listTickets(): Observable<TicketBatchDto[]> {
    return this.http.get<TicketBatchDto[]>(this.ticketUrl);
  }

  /** Fetch all categories in the system */
  listAllCategories(): Observable<TicketCategory[]> {
    return this.http.get<TicketCategory[]>(this.categoryUrl);
  }

  /** Fetch only the categories stocked by a given store */
  listCategoriesByStore(storeId: number): Observable<TicketCategory[]> {
    return this.http.get<TicketCategory[]>(`${this.categoryUrl}/store/${storeId}`);
  }

  listTransactionRecords(): Observable<TicketTransactionRecordDto[]> {
    return this.http.get<TicketTransactionRecordDto[]>(`${this.ticketUrl}-transactions`);
  }

  /** Create a new ticket category */
  createTicketCategory(request: TicketCategoryCreateRequest): Observable<TicketCategory> {
    return this.http.post<TicketCategory>(this.categoryUrl, request);
  }

  /** Update an existing ticket category */
  editTicketCategory(request: TicketCategoryEditRequest): Observable<TicketCategory> {
    return this.http.put<TicketCategory>(this.categoryUrl, request);
  }

  /** Add a category to a store */
  addCategoryToStore(storeId: number, ticketCategoryId: number, startDate: Date, endDate?: Date): Observable<any> {
    const request: AddCategoryRequest = {
      storeId,
      ticketCategoryId,
      startDate: startDate.toISOString(),
      ...(endDate && { endDate: endDate.toISOString() }),
    };
    return this.http.post(`${this.categoryUrl}/store/category/add`, request);
  }

  /** Remove a category from a store */
  removeCategoryFromStore(storeId: number, ticketCategoryId: number): Observable<any> {
    const request: RemoveCategoryRequest = { storeId, ticketCategoryId };
    return this.http.delete(`${this.categoryUrl}/store/category/remove`, { body: request });
  }

  /** Create a new ticket composite */
  createTicketComposite(request: CreateTicketCompositeRequest): Observable<TicketBatch> {
    return this.http.post<TicketBatch>(this.ticketUrl, request);
  }

  /** Record inventory for multiple ticket composites */
  recordInventory(body: RecordInventoryBody): Observable<TicketBatch[]> {
    return this.http.post<TicketBatch[]>(`${this.ticketUrl}/record-inventory`, body);
  }
}
